import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserRole } from '../../hooks/useUserRole';
import { QSAnalysisService } from '../../services';
import SectionWrapper from '../SectionWrapper';
import UniversalBadge from '../common/UniversalBadge';

// === TYPES ===
interface QSAnalysis {
  id: string;
  organization: {
    id: string;
    name: string;
  };
  provider: {
    id: string;
    name: string;
  };
  contractPackage: {
    id: string;
    name: string;
  };
  status: string;
  createdAt: string;
  qsReviewedAt?: string;
  superQsReviewedAt?: string;
  qsReviewer?: {
    name: string;
    email: string;
  };
  superQsReviewer?: {
    name: string;
    email: string;
  };
}

// Sort types
type SortField = 'organization' | 'provider' | 'contractPackage' | 'status' | 'createdAt';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

// === MAIN COMPONENT ===
const QSOverviewPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { role, isLoading: roleLoading } = useUserRole();

  // === STATE ===
  const [analyses, setAnalyses] = useState<QSAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [organizationFilter, setOrganizationFilter] = useState('all');
  const [zeitraumFilter, setZeitraumFilter] = useState<string>('woche'); // Default: letzte 7 Tage
  const [isWide, setIsWide] = useState(false);

  // Sorting state
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'createdAt',
    direction: 'desc'
  });

  // === EFFECTS ===
  useEffect(() => {
    if (roleLoading) return;

    // Prüfe ob Benutzer QS oder SuperQS Berechtigung hat
    if (role !== 'qs' && role !== 'super_qs' && role !== 'admin') {
      setError('Keine Berechtigung für QS-Bereich');
      setLoading(false);
      return;
    }

    fetchAnalyses();
  }, [role, roleLoading, zeitraumFilter]); // Neu laden bei Zeitraum-Änderung

  // === API CALLS ===
  const fetchAnalyses = async () => {
    try {
      setLoading(true);
      const response = await QSAnalysisService.getAllAnalyses(zeitraumFilter);
      setAnalyses(response);
    } catch (err) {
      console.error('Fehler beim Laden der QS-Analysen:', err);
      setError('Fehler beim Laden der Analysen');
    } finally {
      setLoading(false);
    }
  };

  // === HELPER FUNCTIONS ===
  // getStatusInfo wurde entfernt - UniversalBadge wird jetzt verwendet

  const getActionIcon = (status: string) => {
    switch (status) {
      case 'qs_pending':
        return <AssignmentIcon color='inherit' />;
      case 'qs_in_review':
      case 'qs_rejected':
        return <EditIcon />;
      case 'qs_completed':
      case 'qs_approved':
        return <ViewIcon color='inherit' />;
      default:
        return <ViewIcon color='inherit' />;
    }
  };

  const handleAnalysisClick = (analysis: QSAnalysis) => {
    navigate(`/qs/analysis/${analysis.id}`);
  };

  const handleSort = (field: SortField) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const formatDate = (dateString: string) => {
    // Backend sendet UTC-Zeit, aber 2 Stunden hinter lokaler Zeit
    // Füge 2 Stunden hinzu und verwende dann die lokale Zeit des Kunden
    const date = new Date(dateString);
    const correctedTime = new Date(date.getTime() + (2 * 60 * 60 * 1000)); // +2 Stunden

    return correctedTime.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // === FILTERED & SORTED DATA ===
  const filteredAndSortedAnalyses = useMemo(() => {
    if (!analyses || !Array.isArray(analyses)) {
      return [];
    }

    // Filter first (Zeitraum wird bereits vom Backend gefiltert)
    const filtered = analyses.filter(analysis => {
      const matchesSearch =
        analysis.organization.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        analysis.provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        analysis.contractPackage.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || analysis.status === statusFilter;
      const matchesOrganization = organizationFilter === 'all' || analysis.organization.name === organizationFilter;

      return matchesSearch && matchesStatus && matchesOrganization;
    });

    // Then sort
    return filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortConfig.field) {
        case 'organization':
          aValue = a.organization.name.toLowerCase();
          bValue = b.organization.name.toLowerCase();
          break;
        case 'provider':
          aValue = a.provider.name.toLowerCase();
          bValue = b.provider.name.toLowerCase();
          break;
        case 'contractPackage':
          aValue = a.contractPackage.name.toLowerCase();
          bValue = b.contractPackage.name.toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [analyses, searchTerm, statusFilter, organizationFilter, sortConfig]);

  // === UNIQUE VALUES FOR FILTERS ===
  const uniqueOrganizations = useMemo(() => {
    return Array.from(new Set(analyses.map(a => a.organization.name))).sort();
  }, [analyses]);

  const statusOptions = [
    { value: 'qs_pending', label: t('qs:status.qsPending') },
    { value: 'qs_in_review', label: t('qs:status.qsInReview') },
    { value: 'qs_completed', label: t('qs:status.qsCompleted') },
    { value: 'qs_approved', label: t('qs:status.qsApproved') },
    { value: 'qs_rejected', label: t('qs:status.qsRejected') }
  ];

  // === FILTER CONTROLS ===
  const filterControls = (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      gap: 2,
      mb: 3,
      alignItems: { xs: 'stretch', md: 'flex-end' }
    }}>
      <TextField
        size="small"
        placeholder={t('qs:sidebar.searchPlaceholder')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ flex: 1, minWidth: { xs: '100%', md: '300px' } }}
        data-testid="qs-overview-search-input"
      />

      <FormControl size="small" sx={{ minWidth: { xs: '100%', md: '200px' } }}>
        <InputLabel>{t('qs:filters.organization')}</InputLabel>
        <Select
          value={organizationFilter}
          label={t('qs:filters.organization')}
          onChange={(e) => setOrganizationFilter(e.target.value)}
          data-testid="qs-overview-organization-filter"
        >
          <MenuItem value="all">{t('qs:filters.allOrganizations')}</MenuItem>
          {uniqueOrganizations.map((org) => (
            <MenuItem key={org} value={org}>
              <Box display="flex" alignItems="center" gap={1}>
                <BusinessIcon fontSize="small" />
                {org}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: { xs: '100%', md: '200px' } }}>
        <InputLabel>{t('qs:filters.qsStatus')}</InputLabel>
        <Select
          value={statusFilter}
          label={t('qs:filters.qsStatus')}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="qs-overview-status-filter"
        >
          <MenuItem value="all">{t('qs:filters.allStatus')}</MenuItem>
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: { xs: '100%', md: '180px' } }}>
        <InputLabel>{t('dashboard:filters.timeframe')}</InputLabel>
        <Select
          value={zeitraumFilter}
          label={t('dashboard:filters.timeframe')}
          onChange={(e) => setZeitraumFilter(e.target.value)}
          data-testid="qs-overview-timeframe-filter"
        >
          <MenuItem value="alle">{t('dashboard:filters.options.allEntries')}</MenuItem>
          <MenuItem value="heute">{t('dashboard:filters.options.today')}</MenuItem>
          <MenuItem value="woche">{t('dashboard:filters.options.last7Days')}</MenuItem>
          <MenuItem value="monat">{t('dashboard:filters.options.currentMonth')}</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  // === TABLE CONTENT ===
  const tableContent = (
    <TableContainer sx={{
      maxHeight: 'calc(100vh - 300px)', // Höhe für Header, Filter, etc. abziehen
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: '#f1f1f1',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#c1c1c1',
        borderRadius: '4px',
        '&:hover': {
          backgroundColor: '#a8a8a8',
        },
      },
    }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: '1rem',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
              }}
              onClick={() => handleSort('organization')}
              data-testid="qs-overview-table-header-organization"
            >
              {t('qs:table.organization')} {sortConfig.field === 'organization' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
            </TableCell>
            <TableCell
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: '1rem',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
              }}
              onClick={() => handleSort('provider')}
              data-testid="qs-overview-table-header-provider"
            >
              {t('qs:table.provider')} {sortConfig.field === 'provider' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
            </TableCell>
            <TableCell
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: '1rem',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
              }}
              onClick={() => handleSort('contractPackage')}
              data-testid="qs-overview-table-header-contract"
            >
              {t('qs:table.service')} {sortConfig.field === 'contractPackage' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
            </TableCell>
            <TableCell
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: '1rem',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
              }}
              onClick={() => handleSort('status')}
              data-testid="qs-overview-table-header-status"
            >
              {t('qs:table.qsStatus')} {sortConfig.field === 'status' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
            </TableCell>
            <TableCell sx={{ fontSize: '1rem' }} data-testid="qs-overview-table-header-reviewer">{t('qs:table.lastEditedBy')}</TableCell>
            <TableCell sx={{ fontSize: '1rem' }} data-testid="qs-overview-table-header-super-reviewer">{t('qs:table.superQs')}</TableCell>
            <TableCell
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: '1rem',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
              }}
              onClick={() => handleSort('createdAt')}
              data-testid="qs-overview-table-header-created"
            >
              {t('qs:table.created')} {sortConfig.field === 'createdAt' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '1rem' }} data-testid="qs-overview-table-header-action">{t('qs:table.action')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredAndSortedAnalyses.map((analysis) => {
            return (
              <TableRow
                key={analysis.id}
                hover
                onClick={() => handleAnalysisClick(analysis)}
                data-testid={`qs-overview-table-row-${analysis.id}`}
                sx={{
                  cursor: 'pointer',
                  height: '4.25rem',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)'
                  },
                  transition: 'background-color 0.2s ease'
                }}
              >
                <TableCell sx={{
                  fontSize: '1rem',
                  fontWeight: 400
                }}>
                  <Tooltip title={analysis.organization.name} arrow>
                    <Box sx={{
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      maxWidth: '200px',
                      lineHeight: 1.2,
                      maxHeight: '2.4em'
                    }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <BusinessIcon fontSize="small" color="action" />
                        {analysis.organization.name}
                      </Box>
                    </Box>
                  </Tooltip>
                </TableCell>

                <TableCell sx={{
                  fontSize: '1rem',
                  fontWeight: 400
                }}>
                  <Tooltip title={analysis.provider.name} arrow>
                    <Box sx={{
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      maxWidth: '200px',
                      lineHeight: 1.2,
                      maxHeight: '2.4em'
                    }}>
                      {analysis.provider.name}
                    </Box>
                  </Tooltip>
                </TableCell>

                <TableCell sx={{
                  fontSize: '1rem',
                  fontWeight: 400
                }}>
                  <Tooltip title={analysis.contractPackage.name} arrow>
                    <Box sx={{
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      maxWidth: '200px',
                      lineHeight: 1.2,
                      maxHeight: '2.4em'
                    }}>
                      {analysis.contractPackage.name}
                    </Box>
                  </Tooltip>
                </TableCell>

                <TableCell>
                  <UniversalBadge
                    type="qs_status"
                    value={analysis.status}
                    data-testid={`qs-overview-table-row-${analysis.id}-status-badge`}
                  />
                </TableCell>

                <TableCell>
                  {analysis.qsReviewer ? (
                    <Tooltip title={analysis.qsReviewer.email}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {analysis.qsReviewer.name}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {t('qs:table.notAssigned')}
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  {analysis.superQsReviewer ? (
                    <Tooltip title={analysis.superQsReviewer.email}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" />
                        <Typography variant="body2">
                          {analysis.superQsReviewer.name}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      -
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {formatDate(analysis.createdAt)}
                  </Typography>
                </TableCell>

                <TableCell align="right">
                  <Tooltip title={t('qs:table.clickToOpen')}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAnalysisClick(analysis);
                      }}
                      size="small"
                      data-testid={`qs-overview-table-row-${analysis.id}-action-button`}
                    >
                      {getActionIcon(analysis.status)}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}

          {filteredAndSortedAnalyses.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3 }} data-testid="qs-overview-table-empty-state">
                <Typography variant="body2" color="text.secondary">
                  {searchTerm || statusFilter !== 'all' || organizationFilter !== 'all'
                    ? t('qs:overview.noAnalysesFiltered')
                    : t('qs:overview.noAnalysesFound')
                  }
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // === ACTION BUTTONS ===
  const actionButtons = (
    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
      <Button
        variant="contained"
        onClick={fetchAnalyses}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
        sx={{ textTransform: 'none' }}
      >
        {loading ? t('qs:actions.refreshing') : t('qs:actions.refresh')}
      </Button>
    </Box>
  );

  // === ERROR STATE ===
  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // === MAIN RENDER ===
  return (
    <SectionWrapper>
      <Box sx={{
        paddingTop: '6.25rem',
        paddingX: { xs: 2, sm: 3, md: 4 },
        minHeight: '100vh',
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{
            color: 'text.primary',
            mb: 1,
            fontWeight: 400,
            fontSize: '2rem'
          }}>
            {t('qs:overview.title')}
          </Typography>
        </Box>

        {filterControls}

        {/* Tabellen-Card */}
        <Box sx={{
          bgcolor: 'background.paper',
          borderRadius: '8px',
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          border: '1px solid rgb(0 0 0 / 0.05)',
          overflow: 'hidden'
        }}>
          {tableContent}
        </Box>

        {actionButtons}
      </Box>
    </SectionWrapper>
  );
};

export default QSOverviewPage; 